import type { FC } from 'react';
import React, { memo, useCallback, useContext, useState } from 'react';
import { styled, cssMap } from '@compiled/react';
import { graphql } from 'react-relay';
import memoizeOne from 'memoize-one';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import traceUFOHover from '@atlaskit/react-ufo/trace-hover';
import { SpotlightTarget } from '@atlaskit/onboarding';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { isRovoSystemSpaceKey } from '@confluence/route-manager/entry-points/isRovoSystemSpaceKey.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	SPACE_OVERVIEW_SHORTCUT_NAV_3,
	SPACE_OVERVIEW_SHORTCUT,
	UNIVERSAL_SPACE_OVERVIEW_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { RestrictionsDialogQuery } from '@confluence/restrictions/entry-points/RestrictionsDialogQuery.graphql.ts';
import {
	isSpaceNotFoundError,
	isSpaceRestrictedError,
} from '@confluence/restrictions/entry-points/Query.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import {
	PERFORMANCE_SUBJECT_navigation,
	PERFORMANCE_SUBJECT_sideNavigationFMP,
} from '@confluence/performance/entry-points/constants.ts';
import { PerformanceEnd } from '@confluence/performance/entry-points/PerformanceEnd.ts';
import { PerformanceStart } from '@confluence/performance/entry-points/PerformanceStart.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { FORGE_MODULE_SPACE_PAGE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { useExtensionList } from '@confluence/forge-ui/entry-points/useExtensionList.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { FocusToCurrentPageTreeLinkItemSSRInlineScript } from '@confluence/page-tree/entry-points/FocusToCurrentPageTreeLinkItemSSRInlineScript.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsUserViewingSpaceAsAnon } from '@confluence/anonymous-access/entry-points/useIsUserViewingSpaceAsAnon.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { fg } from '@confluence/feature-gating';
import { RtPaidFeatureEducationSpotlightLoadable } from '@confluence/experiment-reverse-trial-paid-feature-education/entry-points/hooks/RtPaidFeatureEducationSpotlightLoadable.tsx';
import type { ApolloInteropMutableHelper } from '@confluence/apollo-relay-interop/entry-points/ApolloInteropMutableHelper.ts';

import { getAllAppLinks } from './space-apps-helpers.ts';
import type {
	SpaceNavigationQuery$data,
	SpaceNavigationQuery$variables,
} from './__generated__/SpaceNavigationQuery.graphql.ts';
import { SPACE_NAVIGATION_METRIC } from './perf.config.ts';

type SpaceNavigationQuery_space = NonNullable<SpaceNavigationQuery$data['space']>;
type SpaceNavigationQuery_spaceSidebarLinks_main = ApolloInteropMutableHelper<
	NonNullable<
		NonNullable<NonNullable<SpaceNavigationQuery$data['spaceSidebarLinks']>['main']>[number]
	>
>;
type SpaceNavigationQuery_spaceSidebarLinks_quick = ApolloInteropMutableHelper<
	NonNullable<
		NonNullable<NonNullable<SpaceNavigationQuery$data['spaceSidebarLinks']>['quick']>[number]
	>
>;

const SpaceNavigationQuery = graphql`
	query SpaceNavigationQuery($spaceKey: String, $isLicensed: Boolean!) {
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				permissionType
			}
		}
		spaceSidebarLinks(spaceKey: $spaceKey) {
			main(includeHidden: true) {
				id
				webItemKey
				webItemCompleteKey
				title
				url
				position
				styleClass
				icon {
					path
					width
					height
					isDefault
				}
				iconClass
				hidden
				canHide
				tooltip
				linkIdentifier
				type
			}
			quick {
				id
				url
				position
				title
				styleClass
			}
		}
		space(key: $spaceKey) {
			id
			key
			name
			type
			status
			icon {
				path
			}
			homepage {
				id
			}
			currentUser @include(if: $isLicensed) {
				isAdmin
				isFavourited
				isWatched
			}
			operations {
				operation
				targetType
			}
			containsExternalCollaborators
			history {
				createdDate
			}
			didContainUserContent
		}
	}
`;
import { SHORTCUTS_KEY, BLOG_KEY } from './webItemCompleteKeys.ts';
import {
	SpaceNavigationContent,
	Nav3SpaceNavigationContent,
} from './SpaceNavigationContentLoaders.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

const SPACE_NAV_ID = 'app-navigation-space-container';

const MANAGE_USERS = 'manage_users';
const MANAGE_NONLICENSED_USERS = 'manage_nonlicensed_users';
const MANAGE_GUEST_USERS = 'manage_guest_users';
const ARCHIVE = 'archive';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceNavigationContainerNav3 = styled.div({
	width: '100%',
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	flexBasis: 'max-content',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-exit-to] > div > div > div': {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
	},
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	nav: {
		backgroundColor: token('elevation.surface'),
		/* dividers below space header*/
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'div:nth-of-type(1)::before': {
			backgroundColor: token('elevation.surface'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'div:nth-of-type(2)::before': {
			right: '15px',
		},
	},
	/* space header*/
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"[data-navheader='true']": {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		padding: '10px 8px 4px 8px !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > div': {
			height: '40px',
			margin: '0px',
		},
	},
});

const spaceNavigationContainerStyles = cssMap({
	root: {
		// Some child elements are absolutely or sticky positioned based on this container - such as borders within the SpaceHeader.
		position: 'relative',
	},
});

/**
 * In nav4, we don't need any of the custom styles from the nav3 variant.
 * We need to pass the same props / HTML attributes through though so we can use `componentWithFG` for feature flagging.
 */
const SpaceNavigationContainerNav4 = ({
	'data-testid': testId,
	'data-vc': dataVc,
	'data-ssr-placeholder-replace': dataSSRPlaceholderReplace,
	onMouseEnter,
	children,
}: {
	'data-testid': string;
	'data-vc': string;
	onMouseEnter: () => void;
	'data-ssr-placeholder-replace'?: string;
	children: React.ReactNode;
}) => (
	<div
		css={spaceNavigationContainerStyles.root}
		data-testid={testId}
		data-vc={dataVc}
		data-ssr-placeholder-replace={dataSSRPlaceholderReplace}
		// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
		onMouseEnter={onMouseEnter}
	>
		{children}
	</div>
);

export type SpaceNavigationProps = {
	isBlogNavigation?: boolean;
	isSpaceSettingsScreen?: boolean;
};

type PreloadSpaceNavigationResult = {
	loading: boolean;
	hasRestrictedError: boolean;
	hasNotFoundError: boolean;
	unhandledError?: ApolloErrorInterop;
	hasSpace: boolean;
	space?: SpaceNavigationQuery_space;
	name?: string;
	iconPath?: string;
	isSpaceAdmin: boolean;
	canManageSomeUsers: boolean;
	homepageId?: string;
	containsExternalCollaborators: boolean;
	main: SpaceNavigationQuery_spaceSidebarLinks_main[];
	quick: SpaceNavigationQuery_spaceSidebarLinks_quick[];
	isUserExternalCollaborator: boolean;
	isSpaceArchived: boolean;
	hasDeleteSpacePermission: boolean;
	hasArchiveSpacePermission: boolean;
};

const createResult = memoizeOne(
	(
		loading: boolean,
		data: SpaceNavigationQuery$data | undefined,
		error: ApolloErrorInterop | undefined,
	) => {
		const result: PreloadSpaceNavigationResult = {
			loading,
			hasSpace: false,
			isSpaceAdmin: false,
			canManageSomeUsers: false,
			containsExternalCollaborators: false,
			main: [],
			quick: [],
			isUserExternalCollaborator: false,
			hasRestrictedError: false,
			hasNotFoundError: false,
			isSpaceArchived: false,
			hasDeleteSpacePermission: false,
			hasArchiveSpacePermission: false,
		};

		if (loading) {
			return result;
		}

		if (error) {
			if (isSpaceRestrictedError(error)) {
				result.hasRestrictedError = true;
			} else if (isSpaceNotFoundError(error)) {
				result.hasNotFoundError = true;
			} else {
				result.unhandledError = error;
			}
			return result;
		}

		result.hasSpace = Boolean(data?.space ?? result.hasSpace);
		result.space = data?.space || undefined;
		result.name = data?.space?.name || undefined;
		result.iconPath = `${CONTEXT_PATH}${data?.space?.icon?.path}`;
		result.isSpaceAdmin = data?.space?.currentUser?.isAdmin ?? result.isSpaceAdmin;
		result.canManageSomeUsers =
			data?.space?.operations?.some(
				(operation) =>
					operation?.operation === MANAGE_USERS ||
					operation?.operation === MANAGE_NONLICENSED_USERS ||
					operation?.operation === MANAGE_GUEST_USERS,
			) ?? result.canManageSomeUsers;
		result.homepageId = data?.space?.homepage?.id || undefined;
		result.containsExternalCollaborators =
			data?.space?.containsExternalCollaborators ?? result.containsExternalCollaborators;

		result.main = data?.spaceSidebarLinks?.main?.filter(isNonNullable) ?? result.main;
		result.quick = data?.spaceSidebarLinks?.quick?.filter(isNonNullable) ?? result.quick;
		result.isUserExternalCollaborator = data?.user?.confluence?.permissionType === 'EXTERNAL';
		result.isSpaceArchived = data?.space?.status === 'archived';
		if (data?.space?.operations) {
			result.hasDeleteSpacePermission = data.space.operations?.some(
				(operation) => operation?.operation === 'delete' && operation?.targetType === 'space',
			);
			result.hasArchiveSpacePermission = data.space.operations?.some(
				(operation) => operation?.operation === ARCHIVE && operation?.targetType === 'space',
			);
		}
		return result;
	},
);

const usePreloadSpaceNavigation = (spaceKey: string, isLicensed: boolean) => {
	const { loading, data, error } = useQueryInterop<
		SpaceNavigationQuery$data,
		SpaceNavigationQuery$variables
	>(SpaceNavigationQuery, {
		errorPolicy: 'all',
		variables: {
			spaceKey,
			isLicensed,
		},
	});

	const result = createResult(loading, data, error);

	// dont want the function to run if the FG is off
	// eslint-disable-next-line confluence-feature-gating/no-preconditioning
	if (fg('aifc_create_enabled') && isRovoSystemSpaceKey(spaceKey)) {
		// Rovo system space has temporary content that the user shouldn't interact with outside of Rovo.
		// This space shouldn't be linked to from anywhere in the app, but the user can access it directly via the url.
		result.hasNotFoundError = true;
	}

	if (loading) return result;

	if (error) {
		if (isSpaceRestrictedError(error)) {
			markErrorAsHandled(error);
		} else if (isSpaceNotFoundError(error)) {
			markErrorAsHandled(error);
		}
	}

	return result;
};

const useOldRestrictionsButtonRefetch = () => {
	const refetch = useCallback((contentId: any) => {
		/**
		 * When the PageTree child component informs us that a page has been
		 * moved in the hierarchy, we execute a fetch of its new permissions
		 * to purge any stale permissions information that the cache may have
		 * held for that page. Not doing so would mean showing stale state for
		 * its padlock icon, and for its restriction dialog, when raised.
		 */
		void queryInterop({
			query: RestrictionsDialogQuery,
			variables: {
				contentId,
			},
			fetchPolicy: 'network-only',
		});
	}, []);

	return refetch;
};

export const SpaceNavigation: FC<SpaceNavigationProps> = memo(
	({ isBlogNavigation, isSpaceSettingsScreen = false }) => {
		const [pageTreeFinishedLoading, setPageTreeFinishedLoading] = useState<boolean>(false);
		const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
		const { isLicensed } = useSessionData();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const isNav4Enabled = useIsNav4Enabled();

		const [stateSpaceKey] = usePageSpaceKey();

		const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
		const isOnEditRoute = useIsEditorPage();

		// @ts-ignore FIXME: `stateSpaceKey` can be `undefined` here, and needs proper handling
		const spaceKey: string = stateSpaceKey;

		const { push, match } = useContext(RoutesContext);

		// On a successful move, tell the restrictions button icon that it may need
		// to be updated
		const onDragDropSuccess = useOldRestrictionsButtonRefetch();

		const onOverviewShortcutTrigger = useCallback(() => {
			push(`/wiki/spaces/${spaceKey}/overview`);
		}, [push, spaceKey]);

		const handleMouseEnter = () => {
			traceUFOHover('space-navigation-hover');

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'entered',
					actionSubject: 'spaceNavigation',
					source: match?.name,
					attributes: {
						navdexPointType: START_TOUCH,
					},
				},
			}).fire();
		};

		const {
			loading,
			hasRestrictedError,
			hasNotFoundError,
			unhandledError,
			hasSpace,
			name,
			space,
			iconPath,
			isSpaceAdmin,
			canManageSomeUsers,
			homepageId,
			containsExternalCollaborators,
			main,
			quick,
			isUserExternalCollaborator,
			isSpaceArchived,
			hasDeleteSpacePermission,
			hasArchiveSpacePermission,
		} = usePreloadSpaceNavigation(spaceKey, isLicensed);

		const {
			loading: loadingForgeApps,
			extensions: forgeApps,
			error: forgeError,
		} = useExtensionList({
			moduleType: FORGE_MODULE_SPACE_PAGE,
			spaceContextOnly: true,
		});

		if (forgeError && !isErrorMarkedAsHandled(forgeError)) {
			getMonitoringClient().submitError(forgeError, {
				attribution: Attribution.ECOSYSTEM,
			});
			markErrorAsHandled(forgeError);
		}

		const { allAppLinks, allVisibleAppLinks, shouldRenderSpaceApps } = getAllAppLinks(
			spaceKey,
			main,
			forgeApps,
			isSpaceAdmin,
		);

		const shortcutsHidden =
			main.filter(
				({ webItemCompleteKey, hidden }) => webItemCompleteKey === SHORTCUTS_KEY && hidden,
			).length > 0;

		// check if blogs should show. Blogs are default off if the webitem does not exist.
		const blogsWebItem = main.find(({ webItemCompleteKey }) => webItemCompleteKey === BLOG_KEY);
		const blogsHidden = !blogsWebItem || blogsWebItem.hidden === true;

		const showShortcutsSection = !loading && !shortcutsHidden;

		const onContentTreeLoadComplete = useCallback(() => {
			setPageTreeFinishedLoading(true);
		}, []);

		const hasSideNavError = Boolean(unhandledError || hasRestrictedError || hasNotFoundError);

		const {
			isUserViewingSpaceAsAnon,
			loading: anonymousAccessQueryLoading,
			error: anonymousAccessQueryError,
		} = useIsUserViewingSpaceAsAnon({ spaceKey, skipQuery: isNav4Enabled || hasSideNavError });

		const spaceId = space?.id || '';
		const spaceName = name || '';
		const isStarred = Boolean(space?.currentUser?.isFavourited);
		const isWatched = Boolean(space?.currentUser?.isWatched);

		const spaceOverviewShortcut = isNav4Enabled
			? SPACE_OVERVIEW_SHORTCUT
			: SPACE_OVERVIEW_SHORTCUT_NAV_3;

		const SpaceNavigationContainer = isNav4Enabled
			? SpaceNavigationContainerNav4
			: SpaceNavigationContainerNav3;

		const navigationProps = {
			loading,
			hasSpace,
			space,
			spaceKey,
			homepageId,
			main,
			quick,
			isSpaceAdmin,
			canManageSomeUsers,
			isBlogNavigation,
			blogsHidden,
			pageTreeFinishedLoading,
			onDragDropSuccess,
			showShortcutsSection,
			shouldRenderSpaceApps,
			allAppLinks,
			allVisibleAppLinks,
			loadingForgeApps,
			anonymousAccessQueryLoading,
			onContentTreeLoadComplete,
		};

		const errorProps = {
			unhandledError,
			hasRestrictedError,
			hasNotFoundError,
			anonymousAccessQueryError,
		};

		const spaceDetails = {
			spaceId,
			spaceName,
			spaceKey,
			homepageId,
			iconPath,
			isStarred,
			isWatched,
			isSpaceAdmin,
			canManageSomeUsers,
			isPersonalSpace: space?.type === 'personal',
			containsExternalCollaborators,
			isUserExternalCollaborator,
			isSpaceArchived,
			hasDeleteSpacePermission,
			hasArchiveSpacePermission,
		};

		return (
			<>
				<SpotlightTarget name="enhance-orientation-spotlight-tour-target-1">
					<SpaceNavigationContainer
						data-testid={SPACE_NAV_ID}
						data-vc="space-navigation"
						onMouseEnter={handleMouseEnter}
						{...ssrPlaceholderIdProp}
					>
						<PerformanceStart
							subject={PERFORMANCE_SUBJECT_navigation}
							subjectId="SpaceViewLoading"
						/>
						{isNav4Enabled ? (
							!loading &&
							hasSpace && (
								<>
									<SpaceNavigationContent
										navigationProps={navigationProps}
										errorProps={errorProps}
										spaceDetails={spaceDetails}
									/>
								</>
							)
						) : (
							<Nav3SpaceNavigationContent
								navigationProps={navigationProps}
								errorProps={errorProps}
								name={name}
								iconPath={iconPath}
								containsExternalCollaborators={containsExternalCollaborators}
								isUserExternalCollaborator={isUserExternalCollaborator}
								isSpaceSettingsScreen={isSpaceSettingsScreen}
								isUserViewingSpaceAsAnon={isUserViewingSpaceAsAnon}
							/>
						)}
						{isNav4Enabled && pageTreeFinishedLoading && fg('rt_paid_feature_education_ab_fg') && (
							<RtPaidFeatureEducationSpotlightLoadable />
						)}
						<GeneralShortcutListener
							key="overview-shortcut"
							deprecatedAccelerator={
								!(isLiveEditMode || isOnEditRoute) ? spaceOverviewShortcut : undefined
							}
							accelerator={UNIVERSAL_SPACE_OVERVIEW_SHORTCUT}
							listener={onOverviewShortcutTrigger}
						/>
						<PerformanceEnd
							subject={PERFORMANCE_SUBJECT_sideNavigationFMP}
							subjectId="SideNavigationFMP"
							includeFeatureFlags
						/>
						{!loading && <PageSegmentLoadEnd key={spaceKey} metric={SPACE_NAVIGATION_METRIC} />}
						{unhandledError && <ErrorDisplay error={unhandledError} />}
						{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
						{(process.env.REACT_SSR || window.__SSR_RENDERED__) && (
							<FocusToCurrentPageTreeLinkItemSSRInlineScript />
						)}
					</SpaceNavigationContainer>
				</SpotlightTarget>
			</>
		);
	},
);

function isNonNullable<T extends object | null | undefined>(x: T): x is NonNullable<T> {
	return Boolean(x);
}
